import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { Timezone } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { FormField } from 'components/FormField/FormField';
import { FormLayout } from 'components/FormLayout/FormLayout';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { updateUser } from 'components/GlobalState/commands/updateUser';
import { TextInput } from 'components/TextInput/TextInput';
import { useLocale } from 'containers/App/LocaleContext';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import type { FC } from 'react';
import deepEqual from 'react-fast-compare';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { timezoneOptions } from 'utils/timezoneOptions';

interface Props {
  onSaveSuccess?: () => void;
}

export interface UserFormValues {
  timezone: Timezone;
}

export const UserForm: FC<Props> = (props) => {
  const {
    userProfile: { userForm: userFormMsg },
  } = useLocale();
  const [submitting, setSubmitting] = useLoadingState();
  const api = useApi();
  const notifications = useNotifications();
  const ctxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
    user: prop('user'),
  });

  const handleSubmit = async (values: UserFormValues) => {
    await withLoadingIndicator(setSubmitting)(async () => {
      await api.request({
        url: '/users/me',
        method: 'PATCH',
        data: { timezone: values.timezone },
      });
      notifications.showNotification({
        type: 'success',
        message: userFormMsg.updated,
      });
      props.onSaveSuccess?.();
      ctxProps.dispatch(updateUser, { patch: { timezone: values.timezone } });
    });
  };

  const formik = useFormik({
    // no validation needed, timezone select is nonClearable
    initialValues: {
      timezone: ctxProps.user.timezone,
    },
    onSubmit: makeFormikOnSubmit(handleSubmit),
    enableReinitialize: true,
  });

  const changed = useMemo(
    () => !deepEqual(formik.initialValues, formik.values),
    [formik.initialValues, formik.values],
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormLayout width='full' noPadding>
        <header>
          <h2>{userFormMsg.title}</h2>
        </header>
        <FormField className='max-w-96' label={userFormMsg.username}>
          <TextInput
            type='text'
            value={ctxProps.user.username ?? ''}
            disabled
          />
        </FormField>
        <FormikSelectField
          className='max-w-96'
          name='timezone'
          options={timezoneOptions}
          formik={formik}
          label={userFormMsg.timezone}
          searchable
          nonClearable
        />
        <footer>
          <Button
            disabled={!changed}
            loading={submitting === 'LOADING'}
            type='submit'
            variant='primary'
            size='sm'
            icon='fa-floppy-disk'
          >
            {userFormMsg.save}
          </Button>
        </footer>
      </FormLayout>
    </form>
  );
};
